import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Divider,
  Button,
} from '@mui/material';
import {
  Link as LinkIcon,
  Share,
  EmojiEvents,
  SportsMma,
  Person,
  Height,
  Scale,
  CalendarToday,
  LocationOn,
  Business,
  FitnessCenter,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { mediaService } from '../../services/mediaService';
import { SocialLink } from '../../types';
import { championshipBeltService, ChampionshipBelt, GOVERNING_BODY_LABELS } from '../../services/championshipBeltService';
import logo1 from '../../Logo1.png';
import backgroundImage from '../../TBC Ring Magazine.png';

const FighterMedia: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [fighterProfile, setFighterProfile] = useState<any>(null);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [fightRecords, setFightRecords] = useState<any[]>([]);
  const [championshipBelts, setChampionshipBelts] = useState<ChampionshipBelt[]>([]);
  const [loadingBelts, setLoadingBelts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadFighterMedia = async () => {
      if (!userId) {
        setError('Invalid fighter ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Load fighter profile
        const { data: profile, error: profileError } = await supabase
          .from('fighter_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (profileError) throw profileError;
        if (!profile) {
          setError('Fighter profile not found');
          setLoading(false);
          return;
        }

        setFighterProfile(profile);

        // Load all data in parallel for better performance
        setLoadingBelts(true);
        const [linksResult, recordsResult, beltsResult] = await Promise.allSettled([
          mediaService.getSocialLinks(profile.id).catch(() => []),
          supabase
            .from('fight_records')
            .select('*')
            .eq('fighter_id', userId)
            .order('date', { ascending: false })
            .limit(10)
            .then(({ data, error }) => error ? [] : (data || [])),
          championshipBeltService.getBeltsByUserId(userId).catch(() => [])
        ]);

        // Set results
        if (linksResult.status === 'fulfilled') {
          setSocialLinks(linksResult.value);
        } else {
          setSocialLinks([]);
        }

        if (recordsResult.status === 'fulfilled') {
          setFightRecords(recordsResult.value);
        } else {
          setFightRecords([]);
        }

        if (beltsResult.status === 'fulfilled') {
          setChampionshipBelts(beltsResult.value);
        } else {
          setChampionshipBelts([]);
        }
        setLoadingBelts(false);
      } catch (err: any) {
        console.error('Error loading fighter media:', err);
        setError(err.message || 'Failed to load fighter media');
      } finally {
        setLoading(false);
      }
    };

    loadFighterMedia();
  }, [userId]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    } catch (err) {
      // Silently fail - clipboard API may not be available
    }
  }, []);

  if (loading) {
    return (
      <>
        {/* Full-screen background layer */}
        <Box
          component="div"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100vh',
            backgroundImage: backgroundImage ? `url("${backgroundImage}")` : 'url("/TBC Ring Magazine.png")',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            zIndex: -1,
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 0,
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      </>
    );
  }

  if (error || !fighterProfile) {
    return (
      <>
        {/* Full-screen background layer */}
        <Box
          component="div"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100vh',
            backgroundImage: backgroundImage ? `url("${backgroundImage}")` : 'url("/TBC Ring Magazine.png")',
            backgroundSize: '100% 100%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            zIndex: -1,
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'relative',
            zIndex: 0,
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
          }}
        >
          <Box sx={{ maxWidth: 600, width: '100%' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Fighter profile not found'}
            </Alert>
            <Typography variant="body2" color="white" textAlign="center">
              This fighter's media profile is not available or has been removed.
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  const record = `${fighterProfile.wins || 0}-${fighterProfile.losses || 0}-${fighterProfile.draws || 0}`;
  const knockouts = fighterProfile.knockouts || 0;

  return (
    <>
      {/* Full-screen background layer */}
      <Box
        component="div"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: backgroundImage ? `url("${backgroundImage}")` : 'url("/TBC Ring Magazine.png")',
          backgroundSize: '100% 100%',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1,
          display: 'block',
        }}
      />
      {/* Content layer */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 0,
          minHeight: '100vh',
          py: 4,
          px: { xs: 2, md: 4 },
        }}
      >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              component="img"
              src={logo1}
              alt="Tantalus Boxing Club Logo"
              sx={{
                height: { xs: 50, md: 70 },
                width: 'auto',
                objectFit: 'contain',
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white' }}>
              Tantalus Ring Magazine Media
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Share />}
            onClick={handleShare}
            sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
          >
            Share
          </Button>
        </Box>

        {/* Fighter Profile Card */}
        <Card sx={{ mb: 3, boxShadow: 6 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
              {/* Left Column: Fighter Info */}
              <Box sx={{ flex: { xs: '1', md: '0 0 66.666%' } }}>
                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  <Avatar
                    sx={{
                      width: { xs: 80, md: 120 },
                      height: { xs: 80, md: 120 },
                      bgcolor: 'primary.main',
                      fontSize: { xs: '2rem', md: '3rem' },
                    }}
                  >
                    {fighterProfile.name?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {fighterProfile.name}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      @{fighterProfile.handle}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={fighterProfile.tier || 'Amateur'} color="primary" />
                      <Chip label={fighterProfile.weight_class || 'N/A'} variant="outlined" />
                      <Chip label={`${fighterProfile.points || 0} pts`} variant="outlined" />
                    </Stack>
                  </Box>
                </Box>

                {/* Record */}
                <Box mb={3}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Record
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="h3" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        {record}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Wins-Losses-Draws
                      </Typography>
                    </Box>
                    {knockouts > 0 && (
                      <Box>
                        <Typography variant="h3" color="error.main" sx={{ fontWeight: 'bold' }}>
                          {knockouts}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Knockouts
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Physical Information */}
                <Box mb={3}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Physical Information
                  </Typography>
                  <Stack spacing={2}>
                    {(fighterProfile.height_feet !== null && fighterProfile.height_feet !== undefined) || 
                     (fighterProfile.height_inches !== null && fighterProfile.height_inches !== undefined) ? (
                      <Box display="flex" alignItems="center">
                        <Height sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Height
                          </Typography>
                          <Typography variant="body1">
                            {fighterProfile.height_feet || 0}'{fighterProfile.height_inches || 0}"
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.weight ? (
                      <Box display="flex" alignItems="center">
                        <Scale sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Weight
                          </Typography>
                          <Typography variant="body1">{fighterProfile.weight} lbs</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.reach ? (
                      <Box display="flex" alignItems="center">
                        <Person sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Reach
                          </Typography>
                          <Typography variant="body1">{fighterProfile.reach}"</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.stance ? (
                      <Box display="flex" alignItems="center">
                        <SportsMma sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Stance
                          </Typography>
                          <Typography variant="body1">
                            {fighterProfile.stance.charAt(0).toUpperCase() + fighterProfile.stance.slice(1).toLowerCase()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.age ? (
                      <Box display="flex" alignItems="center">
                        <Person sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Age
                          </Typography>
                          <Typography variant="body1">{fighterProfile.age} years old</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.hometown ? (
                      <Box display="flex" alignItems="center">
                        <Person sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Hometown
                          </Typography>
                          <Typography variant="body1">{fighterProfile.hometown}</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.nationality ? (
                      <Box display="flex" alignItems="center">
                        <LocationOn sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Nationality
                          </Typography>
                          <Typography variant="body1">{fighterProfile.nationality}</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.fighting_style ? (
                      <Box display="flex" alignItems="center">
                        <SportsMma sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Fighting Style
                          </Typography>
                          <Typography variant="body1">{fighterProfile.fighting_style}</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {(fighterProfile as any)?.birthday ? (
                      <Box display="flex" alignItems="center">
                        <CalendarToday sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Birthday
                          </Typography>
                          <Typography variant="body1">
                            {(() => {
                              try {
                                const birthday = (fighterProfile as any).birthday;
                                if (!birthday) return 'Not set';
                                const dateStr = typeof birthday === 'string' 
                                  ? birthday.split('T')[0] 
                                  : String(birthday);
                                const parts = dateStr.split('-');
                                if (parts.length === 3) {
                                  const year = parseInt(parts[0], 10);
                                  const month = parseInt(parts[1], 10) - 1;
                                  const day = parseInt(parts[2], 10);
                                  const date = new Date(year, month, day);
                                  if (isNaN(date.getTime())) return 'Not set';
                                  return date.toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  });
                                }
                                const date = new Date(dateStr);
                                if (isNaN(date.getTime())) return 'Not set';
                                return date.toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                });
                              } catch {
                                return 'Not set';
                              }
                            })()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.trainer ? (
                      <Box display="flex" alignItems="center">
                        <Person sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Trainer
                          </Typography>
                          <Typography variant="body1">{fighterProfile.trainer}</Typography>
                        </Box>
                      </Box>
                    ) : null}
                    {fighterProfile.gym ? (
                      <Box display="flex" alignItems="center">
                        <Business sx={{ mr: 2, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Gym
                          </Typography>
                          <Typography variant="body1">{fighterProfile.gym}</Typography>
                        </Box>
                      </Box>
                    ) : null}
                  </Stack>
                </Box>

                {/* Bio */}
                {(fighterProfile as any)?.social_media_bio && (
                  <Box mb={3}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Bio
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                      {(fighterProfile as any).social_media_bio}
                    </Typography>
                  </Box>
                )}

                {/* Social Links */}
                {socialLinks.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Follow {fighterProfile.name}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {socialLinks.map((link) => (
                        <Chip
                          key={link.id}
                          label={link.platform}
                          component="a"
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          icon={<LinkIcon />}
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Championship Belts */}
                {loadingBelts ? (
                  <Box display="flex" justifyContent="center" mb={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : championshipBelts.length > 0 && (
                  <Box mb={3}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Championship Belts
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2, 
                        mt: 1,
                        justifyContent: 'flex-start'
                      }}
                    >
                      {championshipBelts.map((belt) => (
                        <Box
                          key={belt.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            maxWidth: '200px',
                          }}
                        >
                          <Box
                            component="img"
                            src={belt.belt_image_url}
                            alt={GOVERNING_BODY_LABELS[belt.governing_body]}
                            sx={{
                              width: '100%',
                              maxWidth: '160px',
                              height: 'auto',
                              objectFit: 'contain',
                              borderRadius: 1,
                              mb: 0.5,
                              boxShadow: 2,
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              textAlign: 'center',
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                              lineHeight: 1.2,
                            }}
                          >
                            {GOVERNING_BODY_LABELS[belt.governing_body]}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Right Column: Creative Fighter Image */}
              <Box sx={{ flex: { xs: '1', md: '0 0 33.333%' } }}>
                {(fighterProfile as any)?.creative_fighter_image_url ? (
                  <Box
                    component="img"
                    src={(fighterProfile as any).creative_fighter_image_url}
                    alt="Creative Fighter"
                    sx={{
                      width: '100%',
                      maxHeight: '500px',
                      objectFit: 'contain',
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '300px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No Creative Fighter image
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Recent Fight Records */}
        {fightRecords.length > 0 && (
          <Card sx={{ boxShadow: 6 }}>
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                Recent Fights
              </Typography>
              <Stack spacing={2}>
                {fightRecords.map((fight) => (
                  <Box key={fight.id}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">
                        vs. {fight.opponent_name}
                      </Typography>
                      <Chip
                        label={fight.result.toUpperCase()}
                        color={fight.result === 'win' ? 'success' : fight.result === 'loss' ? 'error' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {fight.method} {fight.round ? `(Round ${fight.round})` : ''} • {new Date(fight.date).toLocaleDateString()}
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
      </Box>
    </>
  );
};

export default FighterMedia;

